import { SupportedLanguage, translateLiteral } from '../i18n/translations';

export type EnvelopePolygon = {
  x: number[];
  y: number[];
  color?: string;
  note?: string;
  lineDash?: 'solid' | 'dashed';
};

export type CompressorEnvelopeFamily = 'semi-hermetico' | 'hermetico';

export type EnvelopeDefinition = {
  polygons: EnvelopePolygon[];
  displayPolygons: EnvelopePolygon[];
  xlim: [number, number];
  ylim: [number, number];
  xticks: number[];
  yticks: number[];
};

export type OperationEnvelopeStatus = {
  note: string;
  color: string;
  isInside: boolean | null;
};

export type OperationEnvelopeData = {
  envelope: EnvelopeDefinition;
  xTicks: number[];
  yTicks: number[];
  xLabel: string;
  yLabel: string;
  note: string;
  statusColor: string;
  isInside: boolean | null;
};

export const SEMI_HERMETIC_ENVELOPE_DATA: Record<string, EnvelopeDefinition> = {
  R404A: {
    polygons: [
      {
        x: [-40.1, -30.1, 5.1, 5.1, 0.1],
        y: [20.1, 60.1, 60.1, 25.1, 20.1],
      },
      {
        x: [-40.1, -40.1, -35.1, -30.1],
        y: [20.1, 30.1, 60.1, 60.1],
      },
      {
        x: [-40.1, -40.1, -40.1, -35.1],
        y: [30.1, 40.1, 60.1, 60.1],
      },
    ],
    displayPolygons: [
      {
        x: [-40.1, -30.1, 5.1, 5.1, 0.1],
        y: [20.1, 60.1, 60.1, 25.1, 20.1],
        color: '#808080',
        note: 'Operação padrão dentro do envelope.',
      },
      {
        x: [-40.1, -40.1, -35.1, -30.1],
        y: [20.1, 30.1, 60.1, 60.1],
        color: '#0099FF',
        note: 'Atenção: Resfriamento adicional ou temperatura máxima de 0°C do gás de sucção.',
      },
      {
        x: [-40.1, -40.1, -40.1, -35.1],
        y: [30.1, 40.1, 60.1, 60.1],
        color: '#0E2841',
        note: 'Atenção: Resfriamento adicional ou temperatura do gás de sucção limitada com superaquecimento < 20K.',
      },
    ],
    xlim: [-40, 10],
    ylim: [20, 60],
    xticks: [-40, -30, -20, -10, 0, 10],
    yticks: [20, 30, 40, 50, 60],
  },
  R134A: {
    polygons: [
      { x: [-20.1, -20.1, 6.1, 12.1, 12.1, -8.1], y: [47.1, 10.1, 15.1, 25.1, 70.1, 70.1] },
      { x: [-20.1, -20.1, -8.1], y: [47.1, 70.1, 70.1] },
    ],
    displayPolygons: [
      {
        x: [-20.1, -20.1, 6.1, 12.1, 12.1, -8.1],
        y: [47.1, 10.1, 15.1, 25.1, 70.1, 70.1],
        color: '#808080',
        note: 'Operação padrão dentro do envelope.',
      },
      {
        x: [-20.1, -20.1, -8.1],
        y: [47.1, 70.1, 70.1],
        color: '#0099FF',
        note: 'Atenção: Requer resfriamento adicional.',
      },
    ],
    xlim: [-20, 15],
    ylim: [10, 75],
    xticks: [-20, -10, 0, 10],
    yticks: [10, 20, 30, 40, 50, 60, 70],
  },
  R407C: {
    polygons: [
      { x: [-25.1, -25.1, -10.1, 13, 13, 5, -25.1], y: [20.1, 40.1, 55.1, 55.1, 30.1, 20.1, 20.1] },
      { x: [-25.1, -25.1, -15.1, -10.1, -25.1], y: [40.1, 50.1, 55.1, 55.1, 40.1] },
    ],
    displayPolygons: [
      {
        x: [-25.1, -25.1, -10.1, 13, 13, 5],
        y: [20.1, 40.1, 55.1, 55.1, 30.1, 20.1],
        color: '#808080',
        note: 'Operação padrão dentro do envelope.',
      },
      {
        x: [-25.1, -25.1, -15.1, -10.1],
        y: [40.1, 50.1, 55.1, 55.1],
        color: '#0099FF',
        note: 'Atenção: Requer resfriamento adicional.',
      },
    ],
    xlim: [-30, 20],
    ylim: [20, 60],
    xticks: [-30, -20, -10, 0, 10, 20],
    yticks: [20, 30, 40, 50, 60],
  },
  DEFAULT: {
    polygons: [],
    displayPolygons: [],
    xlim: [-40, 15],
    ylim: [10, 75],
    xticks: [-40, -30, -20, -10, 0, 10],
    yticks: [10, 20, 30, 40, 50, 60, 70],
  },
};

export const HERMETIC_ENVELOPE_DATA: Record<string, EnvelopeDefinition> = {
  R22: {
    polygons: [
      { x: [-15.1, 10.1, 10.1, 7.1, -15.1], y: [30.1, 30.1, 65.1, 65.1, 45.1] },
      { x: [-15.1, 7.1, 2.1, -15.1], y: [45.1, 65.1, 65.1, 55.1] },
    ],
    displayPolygons: [
      {
        x: [-15.1, 10.1, 10.1, 7.1, -15.1, -15.1],
        y: [30.1, 30.1, 65.1, 65.1, 45.1, 30.1],
        color: '#808080',
        note: 'Operação padrão dentro do envelope.',
        lineDash: 'solid',
      },
      {
        x: [-15.1, 7.1, 2.1, -15.1, -15.1],
        y: [45.1, 65.1, 65.1, 55.1, 45.1],
        color: '#0099ff',
        note: 'Atenção: Temperatura do gás de sucção limitada com superaquecimento <11K.',
        lineDash: 'dashed',
      },
    ],
    xlim: [-15, 15],
    ylim: [30, 70],
    xticks: [-20, -15, -10, -5, 0, 5, 10],
    yticks: [30, 40, 50, 60],
  },
  R404A_MBP: {
    polygons: [
      { x: [-30.1, 10.1, 10.1, -18.1, -30.1, -30.1], y: [30.1, 30.1, 60.1, 60.1, 35.1, 30.1] },
      { x: [-30.1, -18.1, -25.1, -30.1, -30.1], y: [35.1, 60.1, 60.1, 50.1, 35.1] },
    ],
    displayPolygons: [
      {
        x: [-30.1, 10.1, 10.1, -18.1, -30.1, -30.1],
        y: [30.1, 30.1, 60.1, 60.1, 35.1, 30.1],
        color: '#0099FF',
        note: 'Operação padrão dentro do envelope.',
        lineDash: 'solid',
      },
      {
        x: [-30.1, -18.1, -25.1, -30.1, -30.1],
        y: [35.1, 60.1, 60.1, 50.1, 35.1],
        color: '#074C7A',
        note: 'Atenção: Temperatura do gás de sucção limitada com superaquecimento <11K.',
        lineDash: 'dashed',
      },
    ],
    xlim: [-30, 15],
    ylim: [30, 70],
    xticks: [-35, -30, -25, -20, -15, -10, -5, 0, 5, 10],
    yticks: [30, 40, 50, 60],
  },
  R404A_LBP: {
    polygons: [
      { x: [-30.1, 0.1, 0.1, -20.1, -30.1], y: [30.1, 30.1, 60.1, 60.1, 45.1] },
      { x: [-30.1, -20.1, -30.1], y: [45.1, 60.1, 60.1] },
    ],
    displayPolygons: [
      {
        x: [-30.1, 0.1, 0.1, -20.1, -30.1, -30.1],
        y: [30.1, 30.1, 60.1, 60.1, 45.1, 30.1],
        color: '#0099ff',
        note: 'Operação padrão dentro do envelope.',
        lineDash: 'solid',
      },
      {
        x: [-30.1, -20.1, -30.1, -30.1],
        y: [45.1, 60.1, 60.1, 45.1],
        color: '#074C7A',
        note: 'Atenção: Temperatura do gás de sucção limitada com superaquecimento <11K.',
        lineDash: 'dashed',
      },
    ],
    xlim: [-35, 5],
    ylim: [30, 70],
    xticks: [-35, -30, -25, -20, -15, -10, -5, 0, 5],
    yticks: [30, 35, 40, 45, 50, 55, 60, 65, 70],
  },
  R290: {
    polygons: [
      { x: [-35.1, -35.1, 0.1, 0.1, -26.8], y: [50.1, 35.1, 35.1, 60.1, 60.1] },
      { x: [-26.8, 0.1, 0.1, -20.1], y: [60.1, 60.1, 70.1, 70.1] },
    ],
    displayPolygons: [
      {
        x: [-35.1, -35.1, 0.1, 0.1, -26.8, -35.1],
        y: [50.1, 35.1, 35.1, 60.1, 60.1, 50.1],
        color: '#808080',
        note: 'Operação padrão dentro do envelope.',
        lineDash: 'solid',
      },
      {
        x: [-26.8, 0.1, 0.1, -19.1, -26.8],
        y: [60.1, 60.1, 70.1, 70.1, 60.1],
        color: '#0E2841',
        note: 'Atenção: Requer resfriamento adicional.',
        lineDash: 'dashed',
      },
    ],
    xlim: [-40.1, 5.1],
    ylim: [30, 75],
    xticks: [-35, -30, -25, -20, -15, -10, -5, 0],
    yticks: [35, 40, 45, 50, 55, 60, 65],
  },
  R600: {
    polygons: [
      { x: [-35.1, -35.1, 0.1, 0.1, -26.8], y: [50.1, 35.1, 35.1, 60.1, 60.1] },
      { x: [-26.8, 0.1, 0.1, -20.1], y: [60.1, 60.1, 70.1, 70.1] },
    ],
    displayPolygons: [
      {
        x: [-35.1, -35.1, 0.1, 0.1, -26.8, -35.1],
        y: [50.1, 35.1, 35.1, 60.1, 60.1, 50.1],
        color: '#808080',
        note: 'Operação padrão dentro do envelope.',
        lineDash: 'solid',
      },
      {
        x: [-26.8, 0.1, 0.1, -19.1, -26.8],
        y: [60.1, 60.1, 70.1, 70.1, 60.1],
        color: '#0E2841',
        note: 'Atenção: Requer resfriamento adicional.',
        lineDash: 'dashed',
      },
    ],
    xlim: [-40.1, 5.1],
    ylim: [30, 75],
    xticks: [-35, -30, -25, -20, -15, -10, -5, 0],
    yticks: [35, 40, 45, 50, 55, 60, 65],
  },
  DEFAULT: {
    polygons: [],
    displayPolygons: [],
    xlim: [-40.1, 5.1],
    ylim: [30, 75],
    xticks: [-35, -30, -25, -20, -15, -10, -5, 0],
    yticks: [35, 40, 45, 50, 55, 60, 65],
  },
};

export const getEnvelopeKey = (
  family: CompressorEnvelopeFamily,
  refrigerante: string,
  application?: string
) => {
  if (family === 'hermetico' && refrigerante === 'R404A') {
    return String(application || '').toUpperCase().includes('LBP') ? 'R404A_LBP' : 'R404A_MBP';
  }

  return refrigerante;
};

export const getOperationEnvelope = (
  family: CompressorEnvelopeFamily,
  refrigerante: string,
  application?: string
) => {
  const envelopeData = family === 'hermetico' ? HERMETIC_ENVELOPE_DATA : SEMI_HERMETIC_ENVELOPE_DATA;
  const envelopeKey = getEnvelopeKey(family, refrigerante, application);
  return envelopeData[envelopeKey] || envelopeData.DEFAULT;
};

export const celsiusToFahrenheit = (value: number) => (value * 9) / 5 + 32;
export const fahrenheitToCelsius = (value: number) => ((value - 32) * 5) / 9;

const isFiniteNumber = (value: any) => typeof value === 'number' && Number.isFinite(value);

const translatePolygon = (
  polygon: EnvelopePolygon,
  language: SupportedLanguage
): EnvelopePolygon => ({
  ...polygon,
  note: polygon.note ? translateLiteral(polygon.note, language) : polygon.note,
});

const translateEnvelopeDefinition = (
  envelope: EnvelopeDefinition,
  language: SupportedLanguage
): EnvelopeDefinition => ({
  ...envelope,
  displayPolygons: envelope.displayPolygons.map((polygon) =>
    translatePolygon(polygon, language)
  ),
});

export const isPointInsidePolygon = (x: number, y: number, polygon: EnvelopePolygon) => {
  let inside = false;
  const points = polygon.x.map((px, index) => ({
    x: px,
    y: polygon.y[index],
  }));

  let i = 0;
  let j = points.length - 1;

  for (i = 0; i < points.length; i += 1) {
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;

    const intersect =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / ((yj - yi) || 0.000000000001) + xi;

    if (intersect) {
      inside = !inside;
    }

    j = i;
  }

  return inside;
};

export const getEnvelopeStatus = (
  refrigerante: string,
  evapTempC?: number,
  condTempC?: number,
  language: SupportedLanguage = 'pt-BR',
  family: CompressorEnvelopeFamily = 'semi-hermetico',
  application?: string
): OperationEnvelopeStatus => {
  if (!isFiniteNumber(evapTempC) || !isFiniteNumber(condTempC)) {
    return {
      note: '',
      color: '#000000',
      isInside: null,
    };
  }

  const envelope = getOperationEnvelope(family, refrigerante, application);

  if (!envelope.polygons.length) {
    return {
      note: translateLiteral('Envelope não definido para este fluido.', language),
      color: '#666666',
      isInside: null,
    };
  }

  const insideAny = envelope.polygons.some((polygon) =>
    isPointInsidePolygon(evapTempC, condTempC, polygon)
  );

  if (!insideAny) {
    return {
      note: translateLiteral('AVISO: Ponto de operação FORA do envelope!', language),
      color: 'red',
      isInside: false,
    };
  }

  const displayPolygons = envelope.displayPolygons.slice().reverse();

  const matchedPolygon = displayPolygons.find((polygon) =>
    isPointInsidePolygon(evapTempC, condTempC, polygon)
  );

  const rawNote =
    (matchedPolygon && matchedPolygon.note) ||
    (envelope.displayPolygons[0] && envelope.displayPolygons[0].note) ||
    'Operação padrão dentro do envelope.';

  const note = translateLiteral(rawNote, language);
  const color = rawNote.includes('Atenção') ? 'orange' : 'green';

  return {
    note,
    color,
    isInside: true,
  };
};

export const buildSvgPolygonPoints = (
  polygon: EnvelopePolygon,
  scaleX: (x: number) => number,
  scaleY: (y: number) => number
) =>
  polygon.x
    .map((x, index) => `${scaleX(x)},${scaleY(polygon.y[index])}`)
    .join(' ');

export const generateOperationEnvelopeData = ({
  refrigerante,
  tempUnit,
  evapTemp,
  condTemp,
  language = 'pt-BR',
  family = 'semi-hermetico',
  application,
}: {
  refrigerante: string;
  tempUnit: 'c' | 'f';
  evapTemp?: number;
  condTemp?: number;
  language?: SupportedLanguage;
  family?: CompressorEnvelopeFamily;
  application?: string;
}): OperationEnvelopeData => {
  const rawEnvelope = getOperationEnvelope(family, refrigerante, application);
  const envelope = translateEnvelopeDefinition(rawEnvelope, language);
  const useFahrenheit = tempUnit === 'f';

  const convertValue = (value: number) => {
    if (useFahrenheit) {
      return celsiusToFahrenheit(value);
    }

    return value;
  };

  const status = getEnvelopeStatus(refrigerante, evapTemp, condTemp, language, family, application);

  return {
    envelope,
    xTicks: envelope.xticks.map(convertValue),
    yTicks: envelope.yticks.map(convertValue),
    xLabel: `${translateLiteral('Temperatura de Evaporação', language)} [°${useFahrenheit ? 'F' : 'C'}]`,
    yLabel: `${translateLiteral('Temperatura de Condensação', language)} [°${useFahrenheit ? 'F' : 'C'}]`,
    note: status.note,
    statusColor: status.color,
    isInside: status.isInside,
  };
};
